import { useEffect, useRef, useState } from "react";

import { track } from "@/lib/analytics";
import { useMyExperimentAssignments } from "@/lib/api/hooks";
import {
  markWelcomeVideoDone,
  useWelcomeVideoPending,
} from "@/lib/welcomeVideoFlags";

export const WELCOME_VIDEO_EXPERIMENT_KEY = "portal-shoma-welcome-video-v1";
const ASSIGNMENT_WAIT_MS = 5_000;

/** Keep this independent of guided-tour-v1: control retains the existing flow. */
export function useWelcomeVideoGate({
  patientId,
  eligible,
  forced,
}: {
  patientId: string | undefined;
  eligible: boolean;
  forced: boolean;
}) {
  const pending = useWelcomeVideoPending(patientId);
  const { data, isError } = useMyExperimentAssignments();
  const [timedOutFor, setTimedOutFor] = useState<string | null>(null);
  const exposure = useRef<string | null>(null);
  const timedOut = Boolean(patientId && timedOutFor === patientId);
  const resolved = Boolean(data?.assignments) || isError || timedOut;
  // Use the authoritative query, not the cookie: paused/absent experiments
  // are omitted by the backend, even for someone previously assigned A.
  const variant =
    isError || timedOut
      ? undefined
      : data?.assignments[WELCOME_VIDEO_EXPERIMENT_KEY];

  useEffect(() => {
    if (!eligible || !pending || forced || resolved || !patientId) return;
    // An optional welcome must never strand onboarding on a stalled request.
    const timer = window.setTimeout(
      () => setTimedOutFor(patientId),
      ASSIGNMENT_WAIT_MS,
    );
    return () => window.clearTimeout(timer);
  }, [eligible, pending, forced, resolved, patientId]);

  useEffect(() => {
    if (!eligible || !patientId || !pending || forced || !resolved) return;
    if (variant === "control" || variant === "variant-a") {
      const key = `${patientId}:${variant}`;
      if (exposure.current !== key) {
        exposure.current = key;
        track("$experiment_viewed", {
          experiment_key: WELCOME_VIDEO_EXPERIMENT_KEY,
          variant_key: variant,
          source: "onboarding_welcome_video",
        });
      }
    }
    // Control, paused, absent, invalid or failed assignments consume this
    // first-run opportunity so a later rollout never surprises returners.
    if (variant !== "variant-a") markWelcomeVideoDone(patientId);
  }, [eligible, patientId, pending, forced, resolved, variant]);

  return {
    pending,
    queued:
      eligible &&
      (forced || (pending && (!resolved || variant === "variant-a"))),
    show: eligible && (forced || (pending && variant === "variant-a")),
  };
}
